
import Vue from "vue";
import { checkRowByTableNameAndID, uploadToS3, updateResumeURL } from "@/api";
import _ from "lodash";
import TempStarsTableTitle from "@/components/shared/TempStarsTableTitle";

interface LocationData {
  practiceName: string;
  [key: string]: unknown;
}

// Using Cancelable interface from lodash if available, or defining it locally
interface Cancelable {
  cancel(): void;
  flush(): void;
}

type SearchUserDebounced = (() => Promise<void> | undefined) & Cancelable;

export default Vue.extend({
  name: "Resumes",
  components: {
    TempStarsTableTitle
  },
  data: () => {
    return {
      tableName: "Location" as string,
      checkedName: "" as string,
      userID: 0 as number,
      errors: [] as string[],
      file: null as File | null,
      valid: false as boolean,
      loading: false as boolean,
      submited: false as boolean,
      searchUserDebounced: null as SearchUserDebounced | null,
      fileError: "" as string,
      fileRules: [
        (v: File | null) => !!v || 'File is required',
        (v: File | null) => {
          if (!v) return true;
          const isPdf = v.type === 'application/pdf' || v.name.toLowerCase().endsWith('.pdf');
          return isPdf || 'Only PDF files are allowed';
        }
      ] as Array<(v: File | null) => boolean | string>
    };
  },
  computed: {
    validBtnSubmit() {
      return !this.valid || !this.checkedName || this.userID === 0 || !!this.fileError;
    }
  },
  created() {
    this.searchUserDebounced = _.debounce(async (): Promise<void> => {
      if (!this.userID || this.userID === 0) {
        this.checkedName = "";
        this.errors = [];
        return;
      }
      const data = await checkRowByTableNameAndID({
        id: this.userID,
        tableName: this.tableName
      }) as LocationData[];
      try {
        this.checkErrors(data);
      } catch (error) {
        this.logErrors(error);
      }
    }, 800);
  },
  methods: {
    searchUser(): void {
      if (this.searchUserDebounced) {
        this.searchUserDebounced();
      }
    },
    async uploadsActions(): Promise<void> {
      if (!this.file) {
        return;
      }

      this.loading = true;
      const column = "photoUrl";

      const selectedFile: File = this.file;
      const fileType: string = selectedFile.name.split(".").pop() || "";

      const fileWithNewName = new File([selectedFile], this.userID.toString(), {
        type: selectedFile.type
      });

      try {
        const fileURL: string = await uploadToS3(fileWithNewName, fileType);
        await updateResumeURL({
          tableName: this.tableName,
          id: this.userID,
          column,
          fileURL
        });
        this.submited = true;
        setTimeout(() => {
          this.resetForm();
        }, 2000);
      } catch (error) {
        this.logErrors(error);
      } finally {
        this.loading = false;
      }
    },
    checkErrors(data: LocationData[]): void {
      if (data && data.length > 0 && data[0]) {
        this.errors = [];
        this.checkedName = `Location practice name: ${data[0].practiceName}`;
      } else {
        this.checkedName = "";
        this.errors = ["Location ID not found"];
      }
    },
    resetForm(): void {
      const refs = this.$refs as {
        inputFile?: {
          reset: () => void;
        };
      };
      if (refs.inputFile && typeof refs.inputFile.reset === "function") {
        refs.inputFile.reset();
      }
      this.checkedName = "";
      this.userID = 0;
      this.errors = [];
      this.file = null;
      this.loading = false;
      this.submited = false;
      this.valid = false;
      this.fileError = "";
    },
    onFileChange(): void {
      this.fileError = "";
      this.submited = false;
      
      if (!this.file) {
        this.valid = false;
        return;
      }

      // Check if the file is a PDF
      const isPdf = this.file.type === 'application/pdf' || this.file.name.toLowerCase().endsWith('.pdf');
      
      if (!isPdf) {
        this.fileError = "Only PDF files are allowed. Please select a PDF file.";
        this.valid = false;
        this.file = null;
        // Reset the file input
        const refs = this.$refs as { inputFile?: { reset: () => void } };
        if (refs.inputFile && typeof refs.inputFile.reset === "function") {
          refs.inputFile.reset();
        }
        return;
      }

      this.valid = true;
    },
    logErrors(error: unknown): void {
      console.error(error);
      this.errors = ["Something wrong, please try again"];
    }
  }
});
